import React, { useCallback, useMemo, useState } from 'react'
import { useDetectionExplorerStore, useToggleSelectedDetection } from '../DetectionExplorerStore'
import { DetectionDetailModal, toGalleryItem } from '../Gallery'
import { DetectionResult } from '../get_detections'
import { GroupByLPUI, PlateGroup } from './GroupByLPUI'

/**
 * In group mode the backend already returns detections in the correct order:
 *   - plates ordered by their most-recent detection timestamp (DESC)
 *   - detections within each plate in timestamp DESC
 *   - detections for one plate are contiguous in the response array
 *
 * That ordering is exactly what we need to render. We walk the array once and bucket
 * into a Map keyed by license_plate; Map preserves insertion order, so the grouped
 * output keeps the backend's plate order without an extra sort pass.
 *
 * Null/empty plates are filtered server-side in group mode, but we skip them
 * defensively so a stray row never lands in a bogus group.
 */
const groupByPlate = (items: DetectionResult[]): PlateGroup[] => {
    const map = new Map<string, DetectionResult[]>()
    for (const item of items) {
        const plate = item.license_plate
        if (!plate) continue
        const bucket = map.get(plate)
        if (bucket) {
            bucket.push(item)
        } else {
            map.set(plate, [item])
        }
    }
    return Array.from(map.entries()).map(([plate, detections]) => ({
        plate,
        // Detections within a plate are timestamp DESC, so the first one is the latest.
        latestTimestamp: detections[0].timestamp,
        items: detections.map(toGalleryItem)
    }))
}

type Props = {
    items: DetectionResult[]
}

export const GroupByLPContainer: React.FC<Props> = ({ items }) => {
    const selectedById = useDetectionExplorerStore((s) => s.selectedById)
    const handleToggle = useToggleSelectedDetection(items)

    const [activeDetectionId, setActiveDetectionId] = useState<string | null>(null)

    const groups = useMemo(() => groupByPlate(items), [items])
    const activeDetection = useMemo(
        () => items.find((d) => d.detection_uuid === activeDetectionId) ?? null,
        [items, activeDetectionId]
    )

    const handleOpenDetail = useCallback((id: string) => setActiveDetectionId(id), [])
    const handleCloseDetail = useCallback(() => setActiveDetectionId(null), [])

    return (
        <>
            <GroupByLPUI
                groups={groups}
                selectedIds={Object.keys(selectedById)}
                onToggleSelect={handleToggle}
                onOpenDetail={handleOpenDetail}
            />
            {activeDetection && <DetectionDetailModal detection={activeDetection} onClose={handleCloseDetail} />}
        </>
    )
}
